import React, { useEffect, useState } from "react";
import { Flex, Spin } from "antd";
import { CommunicationHttpService } from "../../services/Http/CommunicationHttpService";

interface Props {
    body: string;
    communicationId: number;
}

/**
 * Renders a communication body that may contain {{IMAGE:id}} placeholders.
 * Each placeholder is replaced with an <img> whose src is fetched from the
 * backend (authenticated) and converted to a blob URL.
 */
export function CommunicationBodyRenderer({ body, communicationId }: Props) {
    const [rendered, setRendered] = useState<React.ReactNode | undefined>(undefined);

    useEffect(() => {
        if (!body) {
            setRendered(null);
            return;
        }

        const IMAGE_PATTERN = /\{\{IMAGE:(\d+)\}\}/g;
        const parts = body.split(IMAGE_PATTERN);

        if (parts.length === 1) {
            // No image placeholders — plain text
            setRendered(<span style={{ whiteSpace: "pre-wrap" }}>{body}</span>);
            return;
        }

        let cancelled = false;
        const blobUrls: string[] = [];

        const ids: number[] = [];
        for (let i = 1; i < parts.length; i += 2) {
            ids.push(Number(parts[i]));
        }

        Promise.all(ids.map((id) => CommunicationHttpService.getImageBlobUrl(id))).then(
            (urls) => {
                if (cancelled) return;
                urls.forEach((u) => blobUrls.push(u));

                const nodes: React.ReactNode[] = [];
                let imgIdx = 0;
                for (let i = 0; i < parts.length; i++) {
                    if (i % 2 === 0) {
                        if (parts[i]) {
                            nodes.push(
                                <span key={`t${i}`} style={{ whiteSpace: "pre-wrap" }}>
                                    {parts[i]}
                                </span>,
                            );
                        }
                    } else {
                        nodes.push(
                            <img
                                key={`img${i}`}
                                src={urls[imgIdx++]}
                                alt=""
                                style={{ maxWidth: "100%", display: "block", margin: "8px 0" }}
                            />,
                        );
                    }
                }
                setRendered(<>{nodes}</>);
            },
        );

        return () => {
            cancelled = true;
            blobUrls.forEach((u) => URL.revokeObjectURL(u));
        };
    }, [body, communicationId]);

    // Still loading images
    if (rendered === undefined && body) {
        return (
            <Flex justify="center" style={{ padding: 16 }}>
                <Spin size="small" />
            </Flex>
        );
    }

    return <div className="comm-body-renderer">{rendered}</div>;
}
